// This file contains the JSA Tab component for the Sampling Event Form. We will use this to render the different JSA components for the Sampling Event Form.

import React, { useState, useMemo, useEffect } from "react";
import { shallow } from "zustand/shallow";
import { useUserState } from "../../../../context/user";
import { useAppStore } from "../../../../store/appStore";
import { JsaTab as SharedJsaTab } from "../../../jsaForm/JsaTab";
import { jsaFormInitialState } from "../../../../store/forms/jsaForm/jsaFormState";
import { JsaTabUtils } from "./jsaTabUtils";
import styles from "./JsaTab.module.css";
import ConfirmationModal from "../../../common/ConfirmationModal";
import CopyJsaModal from "../../../common/CopyJsaModal";

// Individual JSA Tab (now uses shared JSA form)
const JsaTab = ({
  jsaData,
  samplingEventData,
  jsaActionsShared,
  jsaUI,
  allEmployees,
  jsaMode,
  commit,
}) => {
  // Apply incoming websocket diffs to local model + UI when needed
  const { action, jsaId, updatedFields } =
    samplingEventData.jsaUpdatesToMonitor;

  // Build props expected by shared JSA form (V2 only for Sampling Event)
  const delJsaEmployeeList = (dailySummaries) => {
    let delJsaEmployeeList = [];
    for (const dailySummary of dailySummaries) {
      // Splitting string from 01/01/2025 to 2025-01-01
      const dailySummaryDate = dailySummary.date.split("/");
      const dailySummaryDateFormatted =
        dailySummaryDate[2] +
        "-" +
        dailySummaryDate[0] +
        "-" +
        dailySummaryDate[1];
      if (jsaData.jsaBasicInfo.jsaCompletedDate === dailySummaryDateFormatted) {
        const formattedEmployees = dailySummary.employees.map((employee) => ({
          employeeId: employee.employee_id,
          employeeName: employee.employee_name,
        }));
        delJsaEmployeeList.push(...formattedEmployees);
        break;
      }
    }
    return delJsaEmployeeList;
  };

  const jsaProps = {
    allEmployees,
    delJsaEmployeeList: delJsaEmployeeList(samplingEventData.dailySummaries),
    jsaData,
    jsaMode,
    jsaVersion: 2,
    jsaBase: jsaUI?.base || jsaFormInitialState.base,
    jsaCore: jsaUI?.core || jsaFormInitialState.core,
    jsaV1: jsaUI?.v1 || jsaFormInitialState.v1, // unused in SE
    jsaV2: jsaUI?.v2 || jsaFormInitialState.v2,
  };

  return (
    <div className={styles.jsaTabContainer}>
      <div className={styles.jsaTabContent}>
        <SharedJsaTab
          jsaProps={jsaProps}
          actions={jsaActionsShared}
          commit={commit}
          samplingEventForm={true}
        />
      </div>
    </div>
  );
};

// JSA Top-level Tabs
export const JsaTopTabButtons = ({ jsaData, isActive, onClick }) => {
  // Format the date for display in the tab
  const formattedDate = jsaData.jsaBasicInfo?.jsaCompletedDate;

  return (
    <button
      className={`${styles.tabButton} ${isActive ? styles.active : ""}`}
      onClick={() => onClick(jsaData)}
    >
      {formattedDate}
    </button>
  );
};

// Top level JSA Tab
export const SamplingEventJsaTab = ({
  samplingEventData,
  tabProps,
  actions,
}) => {
  const user = useUserState().currentUser;
  const jsaActions = actions?.samplingEventFormActions?.jsaForms;
  const jsaActionsShared = actions?.samplingEventFormActions?.jsaSharedUIOnly;
  const jsaCommit = actions?.samplingEventFormActions?.jsaCommit;
  const allEmployees = useAppStore((s) => s.employees.list, shallow);
  const seUi = tabProps || {};
  const [activeJsaIndex, setActiveJsaIndex] = useState(0);
  // If store-driven UI is available, derive flags; otherwise use local state
  const [localDeleteOpen, setLocalDeleteOpen] = useState(false);
  const [localCopyOpen, setLocalCopyOpen] = useState(false);
  const isDeleteModalOpen = jsaActions
    ? !!tabProps?.jsaUICommon?.isDeleteModalOpen
    : localDeleteOpen;
  const isCopyModalOpen = jsaActions
    ? !!tabProps?.jsaUICommon?.isCopyModalOpen
    : localCopyOpen;
  const [forceUpdate, setForceUpdate] = useState(0);

  console.log("sampling event data in JSA tab:", samplingEventData);

  const jsaListLength = samplingEventData?.jsa?.length;
  useEffect(() => {
    // Checking the length of the jsa list to determine if need to trigger a re-render
    if (jsaListLength !== undefined) {
      setForceUpdate((prev) => prev + 1);
    }
  }, [jsaListLength]);

  // Inside SamplingEventJsaTab component
  const sortedJsas = useMemo(() => {
    if (!samplingEventData?.jsa) return [];

    return [...samplingEventData.jsa].sort((a, b) => {
      const dateA = new Date(a.jsaBasicInfo?.jsaCompletedDate || 0);
      const dateB = new Date(b.jsaBasicInfo?.jsaCompletedDate || 0);
      return dateA - dateB; // Ascending order (oldest first)
    });
  }, [samplingEventData?.jsa, forceUpdate]);

  // Derive activeJsaData from sortedJsas and activeJsaIndex
  const activeJsaData = sortedJsas[activeJsaIndex];
  const effectiveActiveJsaId = activeJsaData?.jsaBasicInfo?.id;

  // Sync store activeJsaId for external consumers if provided
  useEffect(() => {
    if (jsaActions && effectiveActiveJsaId) {
      jsaActions.setActiveJsaId(effectiveActiveJsaId);
    }
  }, [jsaActions, effectiveActiveJsaId]);

  const samplingEventId = samplingEventData.id;

  // Derive UI bucket for the active JSA
  const jsaUIById = seUi?.jsaUIById || {};
  const activeJsaUI = effectiveActiveJsaId
    ? jsaUIById[effectiveActiveJsaId] || jsaFormInitialState
    : jsaFormInitialState;

  // Ensure the activeJsaUI has proper structure, especially v2.controlMeasures and core sections
  const safeActiveJsaUI = {
    base: activeJsaUI?.base || jsaFormInitialState.base,
    core: {
      ...jsaFormInitialState.core,
      ...activeJsaUI?.core,
      // Ensure all core sections have their required properties
      basicInfo: {
        ...jsaFormInitialState.core.basicInfo,
        ...activeJsaUI?.core?.basicInfo,
      },
      racInfo: {
        ...jsaFormInitialState.core.racInfo,
        ...activeJsaUI?.core?.racInfo,
      },
      trainingInfo: {
        ...jsaFormInitialState.core.trainingInfo,
        ...activeJsaUI?.core?.trainingInfo,
      },
      signatureInfo: {
        ...jsaFormInitialState.core.signatureInfo,
        ...activeJsaUI?.core?.signatureInfo,
      },
    },
    v1: activeJsaUI?.v1 || jsaFormInitialState.v1,
    v2: {
      ...jsaFormInitialState.v2,
      ...activeJsaUI?.v2,
      // Ensure controlMeasures always exists with all required properties
      controlMeasures: {
        ...jsaFormInitialState.v2.controlMeasures,
        ...activeJsaUI?.v2?.controlMeasures,
      },
      // Ensure hazards section exists
      hazards: {
        ...jsaFormInitialState.v2.hazards,
        ...activeJsaUI?.v2?.hazards,
      },
    },
  };

  // Ensure a UI bucket exists for the active JSA so shared tab buttons can toggle tabs
  useEffect(() => {
    if (!effectiveActiveJsaId || !jsaActionsShared) return;
    const hasBase = Boolean(safeActiveJsaUI && safeActiveJsaUI.base);
    if (!hasBase) {
      try {
        jsaActionsShared.base.setActiveJsaTab("All");
      } catch {}
    }
  }, [effectiveActiveJsaId, jsaActionsShared, safeActiveJsaUI]);

  // Compute mode based on permissions
  const hasEditPerm =
    user &&
    (user.employee_id === samplingEventData?.teamLead ||
      user.employee_id === samplingEventData?.createdBy ||
      user.access_level === 2);
  const jsaMode = hasEditPerm ? "edit" : "view";

  // JSA Tab Utils
  const jsaTabUtils = useMemo(() => {
    return new JsaTabUtils(samplingEventId, activeJsaData);
  }, [samplingEventId, activeJsaData]);

  // Simplified tab click handler
  const handleJsaTabClick = (index) => {
    setActiveJsaIndex(index);
    const nextId = sortedJsas[index]?.jsaBasicInfo?.id;
    if (jsaActions && nextId) jsaActions.setActiveJsaId(nextId);
  };

  const handleDeleteJsa = () => {
    if (activeJsaData) {
      // Use jsaActions.deleteJsa if available (new WebSocket pattern)
      if (jsaActions && activeJsaData.jsaBasicInfo?.id) {
        jsaActions.deleteJsa(activeJsaData.jsaBasicInfo.id);
        jsaActions.closeDeleteModal();
      } else if (jsaTabUtils) {
        // Fallback to jsaTabUtils (old pattern)
        jsaTabUtils.autosaveDeleteJsa();
        setLocalDeleteOpen(false);
      }

      // Select first JSA after deletion
      if (activeJsaIndex > 0) {
        setActiveJsaIndex(0);
      }
    }
  };

  const handleCopyJsa = (selectedDate) => {
    if (activeJsaData && selectedDate && jsaTabUtils) {
      console.log(activeJsaData, selectedDate, jsaTabUtils);
      jsaTabUtils.autosaveCopyJsa(selectedDate);
      if (jsaActions) {
        jsaActions.closeCopyModal();
      } else {
        setLocalCopyOpen(false);
      }
    }
  };

  return (
    <div className={styles.jsaTabContainer}>
      <div className={styles.jsaTabMenuBar}>
        {/* Separator line */}
        <div className={styles.menuSeparator}></div>

        {/* JSA Date Tabs */}
        {sortedJsas.map((jsa, index) => (
          <JsaTopTabButtons
            key={jsa.jsaBasicInfo?.id || index}
            jsaData={jsa}
            isActive={index === activeJsaIndex}
            onClick={() => handleJsaTabClick(index)}
          />
        ))}
      </div>
      {/* JSA Action Buttons */}
      <div className={styles.jsaActionButtons}>
        {/* <button className={styles.jsaActionButton} onClick={handleNewJsa}>
            + New JSA
          </button> */}
        <button
          className={styles.jsaActionButton}
          onClick={() =>
            jsaActions ? jsaActions.openCopyModal() : setLocalCopyOpen(true)
          }
          disabled={!activeJsaData}
        >
          Copy JSA
        </button>
        <button
          className={styles.jsaActionDeleteButton}
          onClick={() =>
            jsaActions ? jsaActions.openDeleteModal() : setLocalDeleteOpen(true)
          }
          disabled={!activeJsaData || samplingEventData.jsa.length <= 1}
        >
          Delete JSA
        </button>
      </div>
      <div className={styles.jsaTabContent}>
        {sortedJsas.length > 0 && (
          <JsaTab
            key={activeJsaData.jsaBasicInfo?.id || activeJsaIndex}
            jsaData={activeJsaData}
            samplingEventData={samplingEventData}
            jsaActionsShared={jsaActionsShared}
            jsaUI={safeActiveJsaUI}
            allEmployees={allEmployees}
            jsaMode={jsaMode}
            commit={jsaCommit}
          />
        )}
      </div>

      {/* Confirmation Modal for Delete */}
      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onCancel={() =>
          jsaActions ? jsaActions.closeDeleteModal() : setLocalDeleteOpen(false)
        }
        onConfirm={handleDeleteJsa}
        title="Delete JSA"
        message="Are you sure you want to delete this JSA? This action cannot be undone."
        confirmText="Delete"
        confirmButtonClass="deleteButton"
      />

      {/* Copy JSA Modal */}
      <CopyJsaModal
        isOpen={isCopyModalOpen}
        onCancel={() =>
          jsaActions ? jsaActions.closeCopyModal() : setLocalCopyOpen(false)
        }
        onCopy={(selectedDate) => {
          if (jsaActions && activeJsaData?.jsaBasicInfo?.id) {
            // selectedDate is expected as YYYY-MM-DD from modal; if not, adapt upstream
            jsaActions.copyJsa(activeJsaData.jsaBasicInfo.id, selectedDate);
            if (jsaActions) {
              jsaActions.closeCopyModal();
            } else {
              setLocalCopyOpen(false);
            }
          } else {
            handleCopyJsa(selectedDate);
          }
        }}
      />
    </div>
  );
};
